import { useState } from "react"
import Navbar from "../components/Navbar"
import Footer from "../components/Footer"

const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  })

  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isSuccess, setIsSuccess] = useState(false)
  const [activeTab, setActiveTab] = useState("contact")

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }))

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }))
    }
  }

  const validateForm = () => {
    const newErrors = {}

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = "Name is required"
    }

    // Email validation
    if (!formData.email.trim()) {
      newErrors.email = "Email is required"
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid"
    }

    // Subject validation
    if (!formData.subject.trim()) {
      newErrors.subject = "Subject is required"
    }

    // Message validation
    if (!formData.message.trim()) {
      newErrors.message = "Message is required"
    } else if (formData.message.trim().length < 10) {
      newErrors.message = "Message must be at least 10 characters"
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    if (validateForm()) {
      setIsSubmitting(true)

      // Simulate API call
      setTimeout(() => {
        setIsSubmitting(false)
        setIsSuccess(true)

        // Reset form after success
        setTimeout(() => {
          setFormData({
            name: "",
            email: "",
            subject: "",
            message: "",
          })
          setIsSuccess(false)
        }, 3000)
      }, 1500)
    }
  }

  const faqs = [
    {
      question: "How can I contribute temple information?",
      answer:
        "We welcome contributions from temple enthusiasts and experts. You can submit temple information, photos, and historical details through our 'Contribute' form. Our editorial team will review and verify the information before publishing it on our platform.",
    },
    {
      question: "Do you organize temple tours?",
      answer:
        "Yes, we partner with trusted travel agencies to organize curated temple tours and pilgrimages across India. These tours are designed to provide meaningful spiritual experiences with knowledgeable guides. You can find upcoming tours in the 'Pilgrimages' section.",
    },
    {
      question: "Is the information on DivyaTemple verified?",
      answer:
        "Absolutely. Our team of researchers, historians, and temple experts verify all information before it's published. We consult multiple sources, including historical texts, temple authorities, and academic research to ensure accuracy.",
    },
    {
      question: "Can I use DivyaTemple content for my research or publication?",
      answer:
        "Our content is protected by copyright, but we do allow limited use for educational and research purposes with proper attribution. For commercial use or extensive reproduction, please contact us for permission.",
    },
    {
      question: "How can I report inaccurate information?",
      answer:
        "We strive for accuracy, but if you find any incorrect information, please use our 'Report Inaccuracy' form or contact us directly. We appreciate your help in maintaining the quality and reliability of our content.",
    },
  ]
  console.log('I am in contact page')

  return (
    <div className="min-h-screen bg-gradient-to-b from-divine-50 via-white to-sacred-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative py-20 px-4 overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-20"
          style={{ backgroundImage: "url('/placeholder.svg?height=1000&width=1920')" }}
        ></div>
        <div className="absolute inset-0 bg-gradient-to-r from-sacred-900/80 to-sacred-800/80"></div>
        <div className="container mx-auto relative z-10 text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">Contact Us</h1>
          <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
            Have questions about temples or need assistance planning your pilgrimage? We're here to help.
          </p>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-12 px-4 -mt-16 relative z-10">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-sacred-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-sacred-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-sacred-700 mb-2">Our Location</h3>
              <p className="text-gray-600">
                123 Temple Street, Connaught Place
                <br />
                New Delhi, 110001, India
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-sacred-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-sacred-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-sacred-700 mb-2">Phone Number</h3>
              <p className="text-gray-600">
                +91 98765 43210 (General Inquiries)
                <br />
                +91 98765 43211 (Temple Tours)
              </p>
            </div>

            <div className="bg-white rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-sacred-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-8 w-8 text-sacred-600"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-sacred-700 mb-2">Email Address</h3>
              <p className="text-gray-600">
                info@divyatemple.com
                <br />
                support@divyatemple.com
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-12 px-4">
        <div className="container mx-auto max-w-6xl">
          <div className="flex flex-col md:flex-row gap-8">
            {/* Left Column - Contact Form */}
            <div className="md:w-2/3">
              <div className="bg-white rounded-xl shadow-xl overflow-hidden">
                {/* Tabs */}
                <div className="flex border-b border-gray-200">
                  <button
                    onClick={() => setActiveTab("contact")}
                    className={`py-4 px-6 font-medium text-sm sm:text-base ${
                      activeTab === "contact"
                        ? "border-b-2 border-sacred-600 text-sacred-700"
                        : "text-gray-500 hover:text-sacred-600"
                    }`}
                  >
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z"
                        />
                      </svg>
                      Contact Us
                    </div>
                  </button>
                  <button
                    onClick={() => setActiveTab("faq")}
                    className={`py-4 px-6 font-medium text-sm sm:text-base ${
                      activeTab === "faq"
                        ? "border-b-2 border-sacred-600 text-sacred-700"
                        : "text-gray-500 hover:text-sacred-600"
                    }`}
                  >
                    <div className="flex items-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                        />
                      </svg>
                      FAQs
                    </div>
                  </button>
                </div>

                {/* Contact Form */}
                {activeTab === "contact" && (
                  <div className="p-8">
                    <h2 className="text-2xl font-bold text-sacred-700 mb-6">Send Us a Message</h2>

                    {isSuccess ? (
                      <div className="text-center py-8">
                        <div className="w-16 h-16 bg-nature-100 rounded-full flex items-center justify-center mx-auto mb-4">
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            className="h-8 w-8 text-nature-600"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                        <h3 className="text-xl font-semibold text-gray-800 mb-2">Message Sent Successfully!</h3>
                        <p className="text-gray-600 mb-6">Thank you for reaching out. We'll get back to you shortly.</p>
                        <button
                          onClick={() => setIsSuccess(false)}
                          className="bg-sacred-600 hover:bg-sacred-700 text-white px-6 py-2 rounded-lg text-sm font-semibold transition-colors"
                        >
                          Send Another Message
                        </button>
                      </div>
                    ) : (
                      <form onSubmit={handleSubmit} className="space-y-6">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                          <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                              Your Name <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="name"
                              name="name"
                              type="text"
                              value={formData.name}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 rounded-lg border ${
                                errors.name ? "border-red-500" : "border-gray-300"
                              } focus:outline-none focus:ring-2 focus:ring-sacred-500 transition-colors`}
                              placeholder="Enter your full name"
                            />
                            {errors.name && <p className="mt-1 text-sm text-red-500">{errors.name}</p>}
                          </div>

                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                              Email Address <span className="text-red-500">*</span>
                            </label>
                            <input
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleChange}
                              className={`w-full px-4 py-3 rounded-lg border ${
                                errors.email ? "border-red-500" : "border-gray-300"
                              } focus:outline-none focus:ring-2 focus:ring-sacred-500 transition-colors`}
                              placeholder="Enter your email address"
                            />
                            {errors.email && <p className="mt-1 text-sm text-red-500">{errors.email}</p>}
                          </div>
                        </div>

                        <div>
                          <label htmlFor="subject" className="block text-sm font-medium text-gray-700 mb-1">
                            Subject <span className="text-red-500">*</span>
                          </label>
                          <input
                            id="subject"
                            name="subject"
                            type="text"
                            value={formData.subject}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${
                              errors.subject ? "border-red-500" : "border-gray-300"
                            } focus:outline-none focus:ring-2 focus:ring-sacred-500 transition-colors`}
                            placeholder="What is your message about?"
                          />
                          {errors.subject && <p className="mt-1 text-sm text-red-500">{errors.subject}</p>}
                        </div>

                        <div>
                          <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                            Message <span className="text-red-500">*</span>
                          </label>
                          <textarea
                            id="message"
                            name="message"
                            rows="5"
                            value={formData.message}
                            onChange={handleChange}
                            className={`w-full px-4 py-3 rounded-lg border ${
                              errors.message ? "border-red-500" : "border-gray-300"
                            } focus:outline-none focus:ring-2 focus:ring-sacred-500 transition-colors`}
                            placeholder="Please provide details about your inquiry..."
                          ></textarea>
                          {errors.message && <p className="mt-1 text-sm text-red-500">{errors.message}</p>}
                        </div>

                        <div>
                          <button
                            type="submit"
                            disabled={isSubmitting}
                            className={`w-full py-3 px-4 rounded-lg text-white font-medium transition-colors flex items-center justify-center ${
                              isSubmitting ? "bg-sacred-400 cursor-not-allowed" : "bg-sacred-600 hover:bg-sacred-700"
                            }`}
                          >
                            {isSubmitting ? (
                              <>
                                <svg
                                  className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                  xmlns="http://www.w3.org/2000/svg"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                >
                                  <circle
                                    className="opacity-25"
                                    cx="12"
                                    cy="12"
                                    r="10"
                                    stroke="currentColor"
                                    strokeWidth="4"
                                  ></circle>
                                  <path
                                    className="opacity-75"
                                    fill="currentColor"
                                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                  ></path>
                                </svg>
                                Sending Message...
                              </>
                            ) : (
                              <>
                                <svg
                                  xmlns="http://www.w3.org/2000/svg"
                                  className="h-5 w-5 mr-2"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8"
                                  />
                                </svg>
                                Send Message
                              </>
                            )}
                          </button>
                        </div>
                      </form>
                    )}
                  </div>
                )}

                {/* FAQ Section */}
                {activeTab === "faq" && (
                  <div className="p-8">
                    <h2 className="text-2xl font-bold text-sacred-700 mb-6">Frequently Asked Questions</h2>

                    <div className="space-y-6">
                      {faqs.map((faq, index) => (
                        <div key={index} className="bg-sacred-50 rounded-lg p-6">
                          <h3 className="text-lg font-bold text-sacred-700 mb-2">{faq.question}</h3>
                          <p className="text-gray-700">{faq.answer}</p>
                        </div>
                      ))}
                    </div>

                    <div className="mt-8 text-center">
                      <p className="text-gray-600 mb-4">Don't see your question here?</p>
                      <button
                        onClick={() => setActiveTab("contact")}
                        className="bg-sacred-600 hover:bg-sacred-700 text-white px-6 py-3 rounded-lg text-sm font-semibold transition-colors"
                      >
                        Contact Us Directly
                      </button>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column - Map and Office Hours */}
            <div className="md:w-1/3">
              {/* Map */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mb-6">
                <div className="h-64 bg-gray-200 relative">
                  {/* This would be a real map in a production app */}
                  <div className="w-full h-full bg-gray-300 flex items-center justify-center">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-8 w-8 text-gray-500 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                      />
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                      />
                    </svg>
                    <span className="text-gray-600">Map View</span>
                  </div>
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-sacred-700 mb-2">Our Office</h3>
                  <p className="text-gray-600 mb-4">
                    Located in the heart of New Delhi, our office is easily accessible by public transportation.
                  </p>
                  <a
                    href="https://maps.google.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-sacred-600 hover:text-sacred-800 font-medium flex items-center"
                  >
                    Get Directions
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5 ml-1"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M12.293 5.293a1 1 0 011.414 0l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-2.293-2.293a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      />
                    </svg>
                  </a>
                </div>
              </div>

              {/* Office Hours */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-6 w-6 text-sacred-600 mr-2"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                      />
                    </svg>
                    <h3 className="text-xl font-bold text-sacred-700">Office Hours</h3>
                  </div>

                  <ul className="space-y-3">
                    <li className="flex justify-between">
                      <span className="text-gray-600">Monday - Friday:</span>
                      <span className="font-medium">9:00 AM - 6:00 PM</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-600">Saturday:</span>
                      <span className="font-medium">10:00 AM - 4:00 PM</span>
                    </li>
                    <li className="flex justify-between">
                      <span className="text-gray-600">Sunday:</span>
                      <span className="font-medium">Closed</span>
                    </li>
                  </ul>

                  <div className="mt-6 pt-6 border-t border-gray-200">
                    <h4 className="font-bold text-sacred-700 mb-2">Response Time</h4>
                    <p className="text-gray-600">
                      We aim to respond to all inquiries within 24-48 hours during business days.
                    </p>
                  </div>
                </div>
              </div>

              {/* Social Media */}
              <div className="bg-white rounded-xl shadow-lg overflow-hidden mt-6">
                <div className="p-6">
                  <h3 className="text-xl font-bold text-sacred-700 mb-4">Connect With Us</h3>
                  <div className="flex justify-between">
                    <a
                      href="#"
                      className="bg-blue-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-blue-600 transition-colors"
                    >
                      <span className="sr-only">Facebook</span>
                      <span className="text-xl">📱</span>
                    </a>
                    <a
                      href="#"
                      className="bg-sky-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-sky-600 transition-colors"
                    >
                      <span className="sr-only">Twitter</span>
                      <span className="text-xl">📱</span>
                    </a>
                    <a
                      href="#"
                      className="bg-pink-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-pink-600 transition-colors"
                    >
                      <span className="sr-only">Instagram</span>
                      <span className="text-xl">📱</span>
                    </a>
                    <a
                      href="#"
                      className="bg-red-500 text-white w-12 h-12 rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <span className="sr-only">YouTube</span>
                      <span className="text-xl">📱</span>
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Newsletter Section */}
      <section className="py-16 px-4 bg-gradient-to-r from-spiritual-700 to-spiritual-900 text-white">
        <div className="container mx-auto max-w-5xl text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">Subscribe to Our Newsletter</h2>
          <p className="text-xl max-w-3xl mx-auto mb-8">
            Stay updated with the latest temple news, festival dates, and travel guides delivered to your inbox.
          </p>
          <div className="max-w-xl mx-auto">
            <div className="flex flex-col sm:flex-row gap-4">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-grow px-4 py-3 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-divine-500"
              />
              <button className="bg-divine-500 hover:bg-divine-600 text-white px-6 py-3 rounded-lg font-semibold transition-colors whitespace-nowrap">
                Subscribe Now
              </button>
            </div>
            <p className="text-sm text-white/70 mt-4">We respect your privacy. Unsubscribe at any time.</p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  )
}

export default ContactPage